import { useRef } from 'react';

export default function TemplatesSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const examples = [
    {
      title: 'Home Services Website',
      description: 'A professional example designed for Indoor and Outdoor Home Services businesses including landscaping, lawn care, cleaning services, HVAC, plumbing, electrical, and other home service providers.',
      image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/a4908c39dba3a6b021957795eb436804.png',
      link: 'https://homeservicestemplate.jgee1236.workers.dev/',
    },
    {
      title: 'Professional Services Website',
      description: 'A clean and modern website example perfect for accounting firms, doctors offices, consulting firms, law offices, financial advisors, and other professional service businesses.',
      image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/a652e4e53b53a28a9eaf2af3c5a4f098.png',
      link: 'https://professionalservicestemplate.jgee1236.workers.dev/',
    },
    {
      title: 'Food Service / Restaurant Website',
      description: 'A modern restaurant website example designed for menus, food galleries, location information, and online ordering.',
      image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/24f710ba3b0016f0f2ba12c97d6bf223.png',
      link: 'https://foodservicetemplate.jgee1236.workers.dev/',
    },
    {
      title: 'Nail / Beauty Salon Website',
      description: 'A clean and elegant website example designed for beauty salons, nail studios, spas, and wellness centers.',
      image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/d84076bc57ea67365ab2fb054c694478.png',
      link: 'https://salon-template.jgee1236.workers.dev/',
    },
    {
      title: 'Health / Wellness Website',
      description: 'A modern example perfect for chiropractors, massage therapy, yoga studios, physical therapy, and personal trainers.',
      image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/fcf00eb7b47bb0e07fb59804d35f0bc2.png',
      link: 'https://healthwellnessservicestemplate.jgee1236.workers.dev/',
    },
    {
      title: 'Local Retail Website',
      description: 'A professional example designed for boutique shops, gift stores, pet shops, specialty retail stores, and thrift stores.',
      image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/50a54d0d6f24359a2d233ef6d7929f5b.png',
      link: 'https://localretailtemplate.jgee1236.workers.dev/',
    },
  ];

  return (
    <section id="examples" className="pt-28 sm:pt-40 pb-20 lg:pb-32 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 lg:mb-16">
          <div className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <span className="text-sm text-gray-300 font-medium">Nashville Web Design Portfolio</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6">
            Nashville Website Design Examples for Small Businesses
          </h1>
          <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto mb-4 sm:mb-6">
            Preview professional website examples designed for Nashville small businesses. Each example is fast, modern, mobile-friendly, and optimized for local search in Nashville, TN.
          </p>
          <p className="text-sm sm:text-lg text-gray-500 max-w-3xl mx-auto">
            From East Nashville restaurants to Franklin home service companies — these examples show Nashville businesses how to get online quickly without the high cost of traditional development. AI-powered design, delivered in days.
          </p>
        </div>

        {/* Custom websites note */}
        <div className="mb-12 lg:mb-20 max-w-3xl mx-auto">
          <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl px-6 py-5">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-white text-xl"></i>
            </div>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              <span className="font-semibold text-white">All websites we build are fully custom.</span> Every site is designed and built specifically for your business — your branding, your content, your goals. The portfolio links below are simply examples of what we can do, meant to give you a feel for our style and quality.
            </p>
          </div>
        </div>

        {/* Desktop Grid Layout */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {examples.map((example, index) => (
            <div
              key={index}
              className="group bg-gradient-to-b from-gray-900 to-black border border-white/10 rounded-3xl overflow-hidden hover:border-white/30 hover:shadow-2xl hover:shadow-white/5 transition-all duration-300"
            >
              <div className="relative w-full h-80 bg-gray-900 overflow-hidden">
                <img
                  src={example.image}
                  alt={example.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-3">{example.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-6">{example.description}</p>
                <a
                  href={example.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-6 py-3 bg-white text-black font-medium rounded-full hover:bg-gray-200 transition-colors duration-300 whitespace-nowrap cursor-pointer text-center"
                >
                  View Live Example
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Carousel Layout */}
        <div className="md:hidden relative">
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {examples.map((example, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[85vw] snap-center group bg-gradient-to-b from-gray-900 to-black border border-white/10 rounded-3xl overflow-hidden"
              >
                <div className="relative w-full h-64 bg-gray-900 overflow-hidden">
                  <img
                    src={example.image}
                    alt={example.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3">{example.title}</h3>
                  <p className="text-gray-400 leading-relaxed mb-6 text-sm">{example.description}</p>
                  <a
                    href={example.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-6 py-3 bg-white text-black font-medium rounded-full hover:bg-gray-200 transition-colors duration-300 whitespace-nowrap cursor-pointer text-center"
                  >
                    View Live Example
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {examples.map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-white/20"
              ></div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
